import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["events", "openPopover"];
import { Component } from 'react';
import I18n from 'I18n';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import AllDayEventsCount from 'ui-addon-calendars/components/UIDayWeekLayout/AllDayEventsCount';
import { trackCalendarInteraction } from 'ui-addon-calendars/lib/usageTracker';
import UIDayWeekLayout from '../../components/UIDayWeekLayout';
import { isSame, getMomentForWeek, getWeekdaysRange, getMinute } from '../../lib/time';
// @ts-expect-error not yet typed
import { groupOverlappingEvents } from '../../lib/eventParse';
import CALENDAR_VIEW_TYPES from '../../constants/CalendarViewTypes';
import WeekDay from '../WeekDay';
import { Event, AbsoluteWrapper } from './WeekCalendarStyles';
import { eventDefaultLength, eventMinLength as defaultEventMinLength } from '../../constants/sizes';
import { DayOfWeekText } from '../UIDayCalendar/DayCalendarStyles';
import DayOfWeek from '../DayOfWeek';
import { getEventDisplayState, toggleAllDayEvents } from '../../lib/eventDisplay';
import { getEventWeekDetails } from '../../lib/eventWeek';
import { doesEventStartBeforeDay } from '../../lib/event';
import { AllDayEvent, AllDayEventsContainer } from '../../components/AllDayEvents';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export default class UIWeekCalendar extends Component {
  constructor(props) {
    super(props);
    this.state = getEventDisplayState(props.eventDisplay);
  }
  componentDidUpdate(prevProps) {
    if (prevProps.eventDisplay !== this.props.eventDisplay) {
      this.setState(Object.assign({}, getEventDisplayState(this.props.eventDisplay)));
    }
  }
  renderDayOfAllDayEvents({
    dayOfWeek,
    dayIndex,
    hideWeekends = false
  }) {
    const {
      allDayEvents,
      displayDay,
      displayYear,
      timezone
    } = this.props;
    const eventsForDay = allDayEvents.get(dayOfWeek);
    return eventsForDay.filter(Boolean).map((event, index) => {
      const {
        start,
        end
      } = event.props;
      const thisDay = getMomentForWeek(displayYear, displayDay, dayOfWeek, timezone).startOf('day');
      if (doesEventStartBeforeDay({
        start,
        time: thisDay,
        timezone
      }) && dayIndex !== 0) {
        return null;
      }
      const {
        lengthForWeek,
        startPosition
      } = getEventWeekDetails({
        displayDay,
        displayYear,
        dayOfWeek,
        start,
        end,
        timezone,
        hideWeekends
      });
      return /*#__PURE__*/_jsx(AllDayEvent, {
        "data-test-id": "week-calendar-all-day-event",
        index: index,
        dayOfWeek: startPosition,
        length: lengthForWeek,
        children: event
      }, `${startPosition}.${lengthForWeek}.${index}`);
    });
  }
  renderEvent({
    event,
    hour,
    overlapGroupIndex,
    index,
    numberOfChildren,
    dayOfWeek
  }) {
    if (event.props.isPlaceholder) return null;
    const {
      useExactEventTimes,
      eventMinLength,
      timezone
    } = this.props;
    const {
      start,
      length
    } = event.props;
    let extraProps = {};
    if (start) {
      // overflow button doesn't have start time
      const calculatedLength = Math.max(length, useExactEventTimes ? eventMinLength : eventDefaultLength);
      // Ensure event doesn't stretch beyond 24-hour day boundary
      const remainingHoursInDay = 24 - hour;
      const adjustedLength = Math.min(calculatedLength, remainingHoursInDay);
      extraProps = {
        minute: useExactEventTimes && getMinute(start, timezone),
        length: adjustedLength
      };
    }
    return /*#__PURE__*/_jsx(Event, Object.assign({
      "data-test-id": `week-day-event--${dayOfWeek}-${hour}`,
      hour: hour,
      index: index,
      totalEvents: numberOfChildren
    }, extraProps, {
      children: event
    }), `${hour}.${overlapGroupIndex}.${index}`);
  }
  renderDisplayEvents({
    displayEvents,
    hour,
    numberOfChildren,
    dayOfWeek
  }) {
    if (!this.props.useExactEventTimes) {
      return displayEvents.map((event, index) => this.renderEvent({
        event,
        hour,
        overlapGroupIndex: 0,
        index,
        numberOfChildren,
        dayOfWeek
      }));
    }
    const groupedEvents = groupOverlappingEvents(displayEvents);
    return groupedEvents.map((group, groupIndex) => /*#__PURE__*/_jsx("span", {
      children: group.map((event, index) => this.renderEvent({
        event,
        hour,
        overlapGroupIndex: groupIndex,
        index,
        numberOfChildren: group.size,
        dayOfWeek
      }))
    }, groupIndex));
  }
  renderAllEventsForHour(dayOfWeek, hour) {
    const {
      events
    } = this.props;
    const eventsForHour = events.getIn([dayOfWeek, hour]);
    return eventsForHour;
  }
  renderEventsForHour({
    eventsForHour,
    hour,
    dayOfWeek
  }) {
    const {
      maxEventDisplayCount,
      renderOverflow,
      displayDay,
      displayYear,
      timezone
    } = this.props;
    const numberOfEvents = eventsForHour.size;
    const overflowCount = numberOfEvents - maxEventDisplayCount;
    const hasOverflow = overflowCount > 0;
    let displayEvents = eventsForHour.slice(0, numberOfEvents - overflowCount
    // .slice returns an iterable of the underlying type, in this case List<ReactElement>
    // Immutablejs's type for .slice exists only for Iterable (which is inherited by List),
    // unnecessarily widening the return value to any Iterable
    // https://immutable-js.com/docs/v3.8.2/List/#slice()
    );
    if (hasOverflow) {
      displayEvents = displayEvents.push(renderOverflow({
        actualDate: getMomentForWeek(displayYear, displayDay, dayOfWeek, timezone),
        hour,
        dayOfWeek
      }));
    }
    const numberOfChildren = displayEvents.size;
    return /*#__PURE__*/_jsx("span", {
      children: this.renderDisplayEvents({
        displayEvents,
        hour,
        numberOfChildren,
        dayOfWeek
      })
    }, hour);
  }
  renderEventsForDay(events, dayOfWeek) {
    const renderedEvents = events.map((eventsForHour, index) => this.renderEventsForHour({
      eventsForHour,
      hour: index,
      dayOfWeek
    }));
    return /*#__PURE__*/_jsx(AbsoluteWrapper, {
      children: renderedEvents
    });
  }
  renderDay({
    dayOfWeek,
    events
  }) {
    const {
      currentTime,
      _incrementCurrentTime,
      timezone,
      displayDay,
      displayYear,
      dayRowRef
    } = this.props;
    const actualDate = getMomentForWeek(displayYear, displayDay, dayOfWeek, timezone);
    const isCurrentDay = isSame(actualDate, currentTime, timezone, 'day');
    let children = null;
    if (events) {
      const eventsForDay = events.get(dayOfWeek);
      children = this.renderEventsForDay(eventsForDay, dayOfWeek);
    }
    return /*#__PURE__*/_jsx(WeekDay, {
      childRef: dayRowRef,
      isCurrentDay: isCurrentDay,
      droppedMoment: actualDate,
      currentTime: currentTime,
      _incrementCurrentTime: _incrementCurrentTime,
      children: children
    }, dayOfWeek);
  }
  renderHeader() {
    const {
      currentTime,
      displayDay,
      displayYear,
      hideWeekends,
      timezone
    } = this.props;
    const momentLocale = I18n.moment.locale();
    return getWeekdaysRange(hideWeekends, momentLocale).map((dayOfWeek, i) => {
      const date = getMomentForWeek(displayYear, displayDay, dayOfWeek, timezone);
      const isCurrentDay = isSame(date, currentTime, timezone, 'day');
      const weekDayKey = date.locale('en').format('dd');
      return /*#__PURE__*/_jsx(DayOfWeek, {
        formattedDate: date.locale(momentLocale).format('YYYY-MM-DD'),
        dayOfWeekNumber: i,
        weekDayKey: weekDayKey,
        children: /*#__PURE__*/_jsxs(TFlex, {
          align: "center",
          gap: 6,
          children: [/*#__PURE__*/_jsx("span", {
            children: date.locale(momentLocale).format('ddd')
          }), /*#__PURE__*/_jsx(DayOfWeekText, {
            isCurrentDay: isCurrentDay,
            children: date.date()
          })]
        })
      }, dayOfWeek);
    });
  }
  renderDayMarkers(events) {
    const {
      hideWeekends
    } = this.props;
    return getWeekdaysRange(hideWeekends, I18n.moment.locale()).map(dayOfWeek => {
      return this.renderDay({
        dayOfWeek,
        events
      });
    });
  }
  renderAllDayEvents() {
    const {
      hideWeekends,
      maxAllDayEventsIndex
    } = this.props;
    if (maxAllDayEventsIndex === 0) return null;
    const events = getWeekdaysRange(hideWeekends, I18n.moment.locale()).map((dayOfWeek, dayIndex) => {
      const eventsForDay = this.renderDayOfAllDayEvents({
        dayOfWeek,
        dayIndex,
        hideWeekends
      });
      if (this.state.showAllDayEvents === 'collapsed') {
        return /*#__PURE__*/_jsx(AllDayEventsCount, {
          onToggleAllDayEvents: () => this.setState(prev => ({
            showAllDayEvents: toggleAllDayEvents(prev.showAllDayEvents)
          }), () => {
            trackCalendarInteraction({
              action: 'expand all-day events day header',
              viewType: CALENDAR_VIEW_TYPES.WEEK
            });
          }),
          totalEvents: eventsForDay.size,
          view: CALENDAR_VIEW_TYPES.WEEK
        }, dayOfWeek);
      }
      return eventsForDay;
    });
    if (this.state.showAllDayEvents === 'collapsed') {
      return events;
    }
    return /*#__PURE__*/_jsx(AllDayEventsContainer, {
      children: events
    });
  }
  render() {
    const _this$props = this.props,
      {
        events,
        openPopover
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded);
    return /*#__PURE__*/_jsx(UIDayWeekLayout, Object.assign({}, rest, {
      overflowPopoverOpen: !!openPopover,
      header: this.renderHeader(),
      allDayEventsContent: this.renderAllDayEvents(),
      view: CALENDAR_VIEW_TYPES.WEEK,
      showAllDayEvents: this.state.showAllDayEvents,
      showEvents: this.state.showEvents,
      onToggleAllDayEvents: () => this.setState(prev => Object.assign({}, prev, {
        showAllDayEvents: toggleAllDayEvents(prev.showAllDayEvents)
      }), () => {
        trackCalendarInteraction({
          action: this.state.showAllDayEvents === 'expanded' ? 'expand all-day events' : 'collapse all-day events',
          viewType: CALENDAR_VIEW_TYPES.WEEK
        });
      }),
      children: this.renderDayMarkers(events)
    }));
  }
}
UIWeekCalendar.defaultProps = {
  displayDay: 1,
  eventMinLength: defaultEventMinLength
};